import { useCallback, useMemo } from 'react';
import { useInReverseTrial } from '@atlassian/jira-reverse-trial-utils/src/controllers/use-in-reverse-trial/index.tsx';
import {
	PREMIUM_REVERSE_TRIAL_TASK,
	ADVANCED_ROADMAPS_TASK,
} from '@atlassian/jira-onboarding-quickstart-core/src/common/constants.tsx';
import { useJswOnboarding } from '@atlassian/jira-onboarding-quickstart-core/src/services/index.tsx';
import { useOpenChecklist } from '@atlassian/jira-software-onboarding-quickstart/src/controllers/open-checklist/index.tsx';
import { useIsTailoredOnboardingEnabledAndEligible } from '@atlassian/jira-tailored-onboarding/src/controllers/index.tsx';
import { ONBOARDING_STARTED_TIMESTAMP } from '@atlassian/jira-onboarding-core/src/constants.tsx';
import { localStorageProvider } from '@atlassian/jira-onboarding-core/src/utils/index.tsx';

type ReverseTrialInfo = {
	isReady: boolean;
	isInReverseTrial: boolean | undefined;
	onOpenOnboardingMenu: (() => void) | undefined;
	shouldAutoOpen: boolean | undefined;
	onAutoOpen: (() => void) | undefined;
};

/**
 * Condenses some logic from the Reverse Trial Dropdown. This is used
 * to integrate Jira with the AudienceAndTiming code in Platform.
 * The majority of this code can be removed once `jira_rt_quickstart_m1`
 * is cleaned up due to its inclusion in a holdout
 * @returns { isInReverseTrial, onOpenOnboardingMenu }
 */
export const useReverseTrials = (): ReverseTrialInfo => {
	const openChecklist = useOpenChecklist();
	const [{ currentTaskGroupParent }, { changeTaskGroupParent, nextOpenTask }] = useJswOnboarding();

	const { isInReverseTrial, isReady: isRTReady } = useInReverseTrial();

	const {
		isReady: isTOReady,
		isTailoredOnboardingM3EnabledAndEligible,
		isTailoredOnboardingScrumAndSoftwareKanbanEnabled,
	} = useIsTailoredOnboardingEnabledAndEligible();

	const onOpenOnboardingMenu = useCallback(() => {
		const taskGroupToEnter = PREMIUM_REVERSE_TRIAL_TASK;

		if (currentTaskGroupParent !== taskGroupToEnter) {
			changeTaskGroupParent(taskGroupToEnter);
		}

		openChecklist({
			trigger: 'ReverseTrialPopup',
		});

		nextOpenTask(ADVANCED_ROADMAPS_TASK, {
			closeNudge: true,
		});
	}, [currentTaskGroupParent, openChecklist, nextOpenTask, changeTaskGroupParent]);

	const shouldAutoOpen = useMemo(() => {
		if (isInReverseTrial) {
			const onboardingStartedTimestamp = Number(
				localStorageProvider.get(ONBOARDING_STARTED_TIMESTAMP),
			);
			if (onboardingStartedTimestamp) {
				const threeDays = 3 * 24 * 60 * 60 * 1000;
				// If the user has onboarded 3 or more days ago, force open the premium popup
				if (Date.now() - onboardingStartedTimestamp > threeDays) {
					// The check for `jsw_ea_popover_open_vs_close` experiment is performed on platform
					return true;
				}
			}
		}
		return false;
	}, [isInReverseTrial]);

	const onAutoOpen = useCallback(
		() => localStorageProvider.remove(ONBOARDING_STARTED_TIMESTAMP),
		[],
	);

	if (!isRTReady || !isTOReady) {
		return {
			isReady: false,
			isInReverseTrial: undefined,
			onOpenOnboardingMenu: undefined,
			shouldAutoOpen: undefined,
			onAutoOpen: undefined,
		};
	}

	// The check for `jira_rt_quickstart_m1` experiment is performed on platform
	const hidePremiumFeatureItem =
		isTailoredOnboardingM3EnabledAndEligible || isTailoredOnboardingScrumAndSoftwareKanbanEnabled;

	return {
		isReady: true,
		isInReverseTrial,
		onOpenOnboardingMenu: hidePremiumFeatureItem ? undefined : onOpenOnboardingMenu,
		shouldAutoOpen,
		onAutoOpen: shouldAutoOpen ? onAutoOpen : undefined,
	};
};
